import React, { useCallback, useEffect, useState } from 'react';
import { cn } from '../../../../../../utils/classnames';
import { camelizeKeys } from '../../../../../../utils/camelizeKeys';
import BacktestChart from '../../../../../../components/ui/charts/BacktestChart';
import { formatPercentage } from '../../../../../../utils/formatters';
import { ChartLine } from 'lucide-react';
import MediumToggle from '../../../../../../components/ui/toggles/MediumToggle';
import InvestmentPolicyReleaseToggle from './InvestmentPolicyReleaseToggle';

const MODALITY_OPTIONS = ['NACIONAL', 'INTERNACIONAL'];

const MODALITY_TO_PARAM = {
  NACIONAL: 'national',
  INTERNACIONAL: 'international',
};

const InvestmentPolicy = ({ clientId, scope, canWrite }) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedModality, setSelectedModality] = useState('NACIONAL');

  const fetchData = useCallback(() => {
    setLoading(true);

    const modality = MODALITY_TO_PARAM[selectedModality] || 'national';

    fetch(
      `/app/planner/client/${clientId}/dashboard/investment_strategy/investment_policy?scope=${scope}&modality=${modality}`
    )
      .then(async (res) => {
        const responseData = await res.json();

        if (!res.ok) {
          throw responseData;
        }

        return responseData;
      })
      .then((responseData) => {
        const camelData = camelizeKeys(responseData);

        setData(camelData);

        console.log('Fetched investment policy data:', camelData);
      })
      .catch((error) => {
        console.error('Erro ao buscar estratégia de investimentos:', error);
        setData({});
      })
      .finally(() => {
        setLoading(false);
      });
  }, [clientId, scope, selectedModality]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between bg-white rounded-custom20 px-10 py-5 min-w-0">
        <div className="flex items-center gap-3">
          <ChartLine className="text-tealMedium" size={20} strokeWidth={1.75} />

          <span className="text-xl font-medium text-blueDarker">Estratégia de Investimentos</span>

          <MediumToggle
            options={MODALITY_OPTIONS}
            selected={selectedModality}
            onChange={(value) => {
              setSelectedModality(value);
            }}
          />
        </div>

        <InvestmentPolicyReleaseToggle clientId={clientId} canWrite={canWrite} />
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: '1.7fr 1fr' }}>
        <div className="bg-white rounded-custom20 p-10 min-w-0">
          {loading ? (
            <div className="flex h-96 items-center justify-center rounded-custom20 border border-gray-100">
              <span className="text-sm text-gray-400">Carregando...</span>
            </div>
          ) : (
            <BacktestChart backtest={data.backtest} />
          )}
        </div>

        <div className="flex flex-col gap-3 bg-white rounded-custom20 px-5 py-5 min-w-0">
          <h3 className="text-lg font-semibold text-blueDarker px-5 py-1">Análise de Risco</h3>

          <div className="flex flex-col gap-3 text-base">
            <div
              className="flex items-center justify-between bg-blueLight px-5 py-4 text-tealExtraLight3 font-semibold"
              style={{ borderRadius: 12 }}
            >
              <span>Maior retorno mensal no período</span>
              <span>{formatPercentage(data.riskAnalysis?.maxMonthlyReturn * 100, 2)}</span>
            </div>

            <div
              className="flex items-center justify-between bg-blueLight px-5 py-4 text-tealExtraLight3 font-semibold"
              style={{ borderRadius: 12 }}
            >
              <span>Menor retorno mensal no período</span>
              <span>{formatPercentage(data.riskAnalysis?.minMonthlyReturn * 100, 2)}</span>
            </div>

            <div
              className="flex items-center justify-between bg-blueLight px-5 py-4 text-tealExtraLight3 font-semibold"
              style={{ borderRadius: 12 }}
            >
              <span>Retorno acumulado no período</span>
              <span>{formatPercentage(data.riskAnalysis?.cumulativeReturn * 100, 2)}</span>
            </div>

            <div
              className="flex items-center justify-between bg-blueLight px-5 py-4 text-blueMedium"
              style={{ borderRadius: 12 }}
            >
              <span>Drawndown máximo (stress test)</span>
              <span>{formatPercentage(data.riskAnalysis?.maxDrawdown * 100, 2)}</span>
            </div>

            <div
              className="flex items-center justify-between bg-blueLight px-5 py-4 text-blueMedium"
              style={{ borderRadius: 12 }}
            >
              <span>Volatilidade mensal</span>
              <span>{formatPercentage(data.policy?.currentVolatility * 100, 2)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-5" style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="bg-white rounded-custom20 p-7 min-w-0">
          {loading ? (
            <div className="flex items-center justify-center rounded-custom20 border border-gray-100">
              <span className="text-sm text-gray-400">Carregando...</span>
            </div>
          ) : (
            <h3 className="text-lg font-semibold text-blueDarker px-4">Liquidez da carteira inicial</h3>
          )}
        </div>

        <div className="flex flex-col gap-3 bg-white rounded-custom20 px-5 py-5 min-w-0">
          <h3 className="text-lg font-semibold text-blueDarker px-5 py-1">Estratégia de investimentos</h3>

          <div className="flex flex-col gap-3 text-base">
            <div className="bg-blueLight px-5 py-4 text-blueMedium flex flex-col gap-4" style={{ borderRadius: 12 }}>
              <div className="flex items-center gap-5">
                <div
                  className="flex flex-1 items-center bg-white"
                  style={{
                    height: 16,
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className="bg-blueHighlight"
                    style={{
                      width: `${data.policy?.maxAllowedVolatility || 0}%`,
                      height: 10,
                      marginLeft: '3px',
                      marginRight: '3px',
                      borderRadius: 999,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>

                <span className="text-sm text-blueDarker">
                  {formatPercentage(data.policy?.maxAllowedVolatility, 2)}
                </span>
              </div>

              <span className="block text-lg text-blueDarker">Volatilidade máxima permitida</span>
            </div>

            <div className="bg-blueLight px-5 py-4 text-blueMedium flex flex-col gap-4" style={{ borderRadius: 12 }}>
              <div className="flex items-center gap-5">
                <div
                  className="flex flex-1 items-center bg-white"
                  style={{
                    height: 16,
                    borderRadius: 999,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    className="bg-tealLight"
                    style={{
                      width: `${(data.policy?.currentVolatility || 0) * 100}%`,
                      height: 10,
                      marginLeft: '3px',
                      marginRight: '3px',
                      borderRadius: 999,
                      transition: 'width 0.3s ease',
                    }}
                  />
                </div>

                <span className="text-sm text-blueDarker">
                  {formatPercentage(data.policy?.currentVolatility * 100, 2)}
                </span>
              </div>

              <span className="block text-lg text-blueDarker">Volatilidade atual</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvestmentPolicy;
